import React from "react";
import { Helmet } from "react-helmet";
import { useStore } from "../../context/StoreContext";
import { SKELETON_STYLES } from "../../pages/StoreBuilder/skeletonStyles";

/* The navigation bar before the store's settings arrive.

   It shares `.sk-b` with the home page's section skeletons so the whole loading
   page shimmers as one surface.

   Every block is the footprint of the control it stands in for (see HEADER_STYLES
   in ./index.js): a 40px rounded logo tile, not a 54px disc, and 38px chips, not
   24px ones.

   No announcement-strip block above it, deliberately: the strip is optional, most
   stores have none, and a 36px band that draws and then vanishes is the layout
   jump, not the cure. */
const SkeletonBox = ({ width, height, radius = 4, className = "", style }) => (
  <div
    className={`sk-b ${className}`}
    style={{ width, height, borderRadius: radius, flex: "none", ...style }}
  />
);

const HeaderSkeleton = () => {
  const { storeDetails } = useStore();

  if (storeDetails?.status && storeDetails?.status === 'INITIAL_PROCESS_NOT_COMPLETED') {
    return null;
  }

  return (
    <>
      <Helmet>
        <style>{SKELETON_STYLES}</style>
      </Helmet>
      <div className="flex items-center justify-between h-16" aria-hidden="true">
        {/* Brand: the logo tile plus the two lines of the store name block. */}
        <div className="flex items-center gap-[11px] min-w-0">
          <SkeletonBox width="40px" height="40px" radius={8} />
          <div className="min-w-0">
            <SkeletonBox width="132px" height="17px" />
            <SkeletonBox width="84px" height="10px" style={{ marginTop: 5 }} />
          </div>
        </div>

        {/* Nav links — the same five-ish rows the real bar centres, hidden at
            the same breakpoint the real nav is. */}
        <div className="hidden nav:flex items-center gap-[32px]">
          <SkeletonBox width="46px" height="14px" />
          <SkeletonBox width="62px" height="14px" />
          <SkeletonBox width="76px" height="14px" />
          <SkeletonBox width="54px" height="14px" />
          <SkeletonBox width="78px" height="14px" />
        </div>

        {/* Tools: search and cart chips on every width, burger below nav. */}
        <div className="flex items-center gap-[10px]">
          <SkeletonBox width="38px" height="38px" radius="50%" />
          <SkeletonBox width="38px" height="38px" radius="50%" />
          <SkeletonBox width="38px" height="38px" radius={8} className="nav:hidden" />
        </div>
      </div>
    </>
  );
};

export default HeaderSkeleton;
